// HUD layout schema: built-in element list, anchors, per-element layout and the
// 3–5 band value paint. Pure data + validation (no DOM).

export type HudAnchor =
  | "auto"
  | "top-left"
  | "top"
  | "top-right"
  | "left"
  | "center"
  | "right"
  | "bottom-left"
  | "bottom"
  | "bottom-right";

export type HudValueSource = "health" | "armor" | "ammo";

/** Sources available to custom value elements (all client-known/self data). */
export type HudSource = HudValueSource | "stack" | "speed";

export type HudCustomKind = "bar" | "ring" | "pips" | "spark";

export interface HudCustomSpec {
  kind: HudCustomKind;
  source: HudSource;
  min: number;
  max: number;
  /** Bar / pips / spark box. */
  width: number;
  height: number;
  /** Bar thickness / ring stroke. */
  thickness: number;
  /** Ring outer radius. */
  radius: number;
  /** Pips count. */
  segments: number;
  direction: "right" | "left" | "up" | "down";
  showValue: boolean;
  valueSize: number;
  suffix: string;
  label: string;
  labelSize: number;
  /** Spark sampling interval (ms). */
  sampleMs: number;
}

export interface HudBand {
  /** Lowest value of this band (bands are sorted ascending). */
  min: number;
  color: string;
}

export interface HudValuePaint {
  enabled: boolean;
  mode: "steps" | "gradient";
  /** 3–5 bands. */
  bands: HudBand[];
}

export interface HudLook {
  background: string | null;
  backgroundOpacity: number;
  borderColor: string | null;
  borderWidth: number;
  borderRadius: number;
  padding: number;
  textColor: string | null;
  /** Empty part of a bar/ring/pips. */
  trackColor: string | null;
  trackOpacity: number;
  fillOpacity: number;
  barRadius: number;
}

export interface HudElementLayout {
  id: string;
  /** `auto` keeps the element's shipped CSS position (no override). */
  anchor: HudAnchor;
  /** Inward from the anchor: positive X moves away from a side edge. */
  offset: { x: number; y: number };
  scale: number;
  opacity: number;
  z: number | null;
  visible: boolean;
  /** Removed from the HUD (built-ins keep their settings so they can return). */
  removed?: boolean;
  /** Hidden outside race-like modes (race / freestyle / defrag). */
  raceOnly: boolean;
  look: HudLook;
  color: HudValuePaint | null;
  /** Present = a custom value element rendered by the runtime. */
  custom?: HudCustomSpec | null;
}

export interface HudDocument {
  version: 1;
  elements: HudElementLayout[];
}

export interface HudLayoutStore {
  version: 1;
  playing: HudDocument;
  spectate: HudDocument;
  /** Show the playing HUD while spectating (ignores the spectate document). */
  spectateUsePlayer: boolean;
}

export interface HudElementDef {
  id: string;
  label: string;
  group: "Combat" | "Race" | "Match" | "Events" | "Debug" | "Spectate";
  source?: HudValueSource;
}

export const HUD_ELEMENTS: HudElementDef[] = [
  { id: "hud-health", label: "Health", group: "Combat", source: "health" },
  { id: "hud-armor", label: "Armor", group: "Combat", source: "armor" },
  { id: "hud-ammo", label: "Ammo", group: "Combat", source: "ammo" },
  { id: "hud-weapon", label: "Weapon name", group: "Combat" },
  { id: "timer", label: "Race timer", group: "Race" },
  { id: "checkpoint-hud", label: "Checkpoint deltas", group: "Race" },
  { id: "angle-hud", label: "Angle readout", group: "Race" },
  { id: "accel-bar", label: "Accel bar", group: "Race" },
  { id: "speed", label: "Speed", group: "Race" },
  { id: "cgaz-hud", label: "cgaz strip", group: "Race" },
  { id: "recall-hud", label: "Recall info", group: "Race" },
  { id: "match-hud", label: "Match info", group: "Match" },
  { id: "flag-hud", label: "Flag status", group: "Match" },
  { id: "vote-hud", label: "Vote status", group: "Match" },
  { id: "killfeed", label: "Kill feed", group: "Events" },
  { id: "announce", label: "Announcement", group: "Events" },
  { id: "coords", label: "Position / velocity", group: "Debug" },
  { id: "fps", label: "FPS", group: "Debug" },
  { id: "netgraph", label: "Net graph", group: "Debug" },
  { id: "spectate-hud", label: "Spectate info", group: "Spectate" },
];

export const HUD_ELEMENT_IDS = HUD_ELEMENTS.map((e) => e.id);
export const HUD_ELEMENT_BY_ID = new Map(HUD_ELEMENTS.map((e) => [e.id, e]));

export const DEFAULT_BANDS: Record<HudSource, HudBand[]> = {
  health: [
    { min: 0, color: "#ff3040" },
    { min: 70, color: "#ffd23f" },
    { min: 120, color: "#59e06b" },
  ],
  armor: [
    { min: 0, color: "#ff3040" },
    { min: 50, color: "#ffd23f" },
    { min: 100, color: "#59e06b" },
  ],
  ammo: [
    { min: 0, color: "#ff3040" },
    { min: 8, color: "#ffd23f" },
    { min: 20, color: "#59e06b" },
  ],
  stack: [
    { min: 0, color: "#ff3040" },
    { min: 100, color: "#ffd23f" },
    { min: 200, color: "#59e06b" },
  ],
  speed: [
    { min: 0, color: "#7f8a99" },
    { min: 1000, color: "#ffd23f" },
    { min: 2500, color: "#59e06b" },
  ],
};

export const CUSTOM_KIND_LABELS: Record<HudCustomKind, string> = {
  bar: "Bar",
  ring: "Ring",
  pips: "Pips",
  spark: "Sparkline",
};

export const SOURCE_LABELS: Record<HudSource, string> = {
  health: "Health",
  armor: "Armor",
  ammo: "Ammo",
  stack: "Stack (hp+ap)",
  speed: "Speed",
};

export function sourceMax(source: HudSource): number {
  return source === "health" ? 150 : source === "armor" ? 100 : source === "ammo" ? 30 : source === "stack" ? 250 : 5000;
}

export function sourceTag(source: HudSource): string {
  return source === "health" ? "HP" : source === "armor" ? "AP" : source === "ammo" ? "AMMO" : source === "stack" ? "STACK" : "UPS";
}

export const DEFAULT_ANCHORS: Record<string, HudAnchor> = {
  "hud-health": "bottom-left",
  "hud-armor": "bottom-left",
  "hud-ammo": "bottom-right",
  "hud-weapon": "bottom-right",
  timer: "top",
  "checkpoint-hud": "top",
  "angle-hud": "auto",
  "accel-bar": "auto",
  speed: "auto",
  "cgaz-hud": "auto",
  "recall-hud": "top",
  "match-hud": "top",
  "flag-hud": "top",
  "vote-hud": "top",
  killfeed: "top-right",
  announce: "top",
  coords: "top-left",
  fps: "top-right",
  netgraph: "top-right",
  "spectate-hud": "bottom",
};

const ANCHORS: HudAnchor[] = [
  "auto",
  "top-left",
  "top",
  "top-right",
  "left",
  "center",
  "right",
  "bottom-left",
  "bottom",
  "bottom-right",
];

const HEX_RE = /^#[0-9a-f]{6}$/i;
export const isHex = (v: unknown): v is string => typeof v === "string" && HEX_RE.test(v);

export function clamp(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v));
}

export function defaultLook(): HudLook {
  return {
    background: null,
    backgroundOpacity: 0.35,
    borderColor: null,
    borderWidth: 0,
    borderRadius: 2,
    padding: 0,
    textColor: null,
    trackColor: null,
    trackOpacity: 0.16,
    fillOpacity: 1,
    barRadius: 2,
  };
}

export function defaultElement(id: string): HudElementLayout {
  return {
    id,
    anchor: "auto",
    offset: { x: 0, y: 0 },
    scale: 1,
    opacity: 1,
    z: null,
    visible: true,
    removed: false,
    raceOnly: RACE_ONLY_DEFAULT.has(id),
    look: defaultLook(),
    color: null,
  };
}

/** Elements that ship visible only in race-like modes (legacy "Race only"). */
const RACE_ONLY_DEFAULT = new Set(["angle-hud", "accel-bar", "speed", "cgaz-hud"]);

export function defaultDocument(): HudDocument {
  const elements = HUD_ELEMENTS.map((e) => defaultElement(e.id));
  for (const el of elements) {
    if (RACE_ONLY_DEFAULT.has(el.id)) el.raceOnly = true;
  }
  return { version: 1, elements };
}

export function defaultStore(): HudLayoutStore {
  return {
    version: 1,
    playing: defaultDocument(),
    spectate: defaultDocument(),
    spectateUsePlayer: false,
  };
}

export function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

let customSeq = 0;

export function defaultCustom(kind: HudCustomKind, source?: HudSource, cascade = 0): HudElementLayout {
  const src: HudSource = source ?? (kind === "bar" ? "health" : kind === "ring" ? "ammo" : kind === "pips" ? "armor" : "speed");
  const spec: HudCustomSpec = {
    kind,
    source: src,
    min: 0,
    max: sourceMax(src),
    width: kind === "pips" ? 120 : kind === "spark" ? 140 : 160,
    height: kind === "spark" ? 36 : kind === "pips" ? 12 : 14,
    thickness: kind === "ring" ? 5 : 2,
    radius: 26,
    segments: kind === "pips" ? 10 : 0,
    direction: "right",
    showValue: kind === "bar" || kind === "ring",
    valueSize: 12,
    suffix: "",
    label: sourceTag(src),
    labelSize: 9,
    sampleMs: 100,
  };
  customSeq = (customSeq + 1) % 1000;
  // Sensible starting corners: bars/pips bottom-left, ring bottom-right,
  // sparkline top-right. Cascade repeated adds so they don't stack exactly.
  const anchor: HudAnchor =
    kind === "ring" ? "bottom-right" : kind === "spark" ? "top-right" : "bottom-left";
  const baseY = kind === "pips" ? 84 : kind === "spark" ? 60 : 56;
  return {
    id: `hud-custom-${Date.now().toString(36)}-${customSeq.toString(36)}`,
    anchor,
    offset: { x: 24 + cascade * 18, y: baseY + cascade * 18 },
    scale: 1,
    opacity: 1,
    z: null,
    visible: true,
    raceOnly: false,
    look: {
      ...defaultLook(),
      trackColor: kind === "bar" ? "#000000" : "#ffffff",
      trackOpacity: kind === "bar" ? 0.4 : 0.16,
      barRadius: 2,
    },
    color: { enabled: true, mode: "steps", bands: DEFAULT_BANDS[src].map((b) => ({ ...b })) },
    custom: spec,
  };
}

function normalizeCustom(raw: unknown): HudCustomSpec | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const kinds: HudCustomKind[] = ["bar", "ring", "pips", "spark"];
  const sources: HudSource[] = ["health", "armor", "ammo", "stack", "speed"];
  if (!kinds.includes(o.kind as HudCustomKind)) return null;
  if (!sources.includes(o.source as HudSource)) return null;
  const num = (v: unknown, d: number, min: number, max: number): number =>
    typeof v === "number" && Number.isFinite(v) ? clamp(v, min, max) : d;
  const dirs = ["right", "left", "up", "down"];
  return {
    kind: o.kind as HudCustomKind,
    source: o.source as HudSource,
    min: num(o.min, 0, -10000, 10000),
    max: num(o.max, 100, -10000, 10000),
    width: num(o.width, 140, 8, 800),
    height: num(o.height, 14, 4, 800),
    thickness: num(o.thickness, 3, 1, 64),
    radius: num(o.radius, 26, 4, 240),
    segments: Math.round(num(o.segments, 10, 1, 64)),
    direction: dirs.includes(o.direction as string) ? (o.direction as HudCustomSpec["direction"]) : "right",
    showValue: o.showValue !== false,
    valueSize: num(o.valueSize, 12, 6, 96),
    suffix: typeof o.suffix === "string" ? o.suffix.slice(0, 8) : "",
    label: typeof o.label === "string" ? o.label.slice(0, 16) : "",
    labelSize: num(o.labelSize, 9, 6, 48),
    sampleMs: num(o.sampleMs, 100, 20, 2000),
  };
}

function normalizeColor(raw: unknown): HudValuePaint | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  if (o.enabled !== true) return null;
  const mode = o.mode === "gradient" ? "gradient" : "steps";
  const rawBands = Array.isArray(o.bands) ? o.bands : [];
  const bands: HudBand[] = [];
  for (const b of rawBands) {
    if (!b || typeof b !== "object") continue;
    const bb = b as Record<string, unknown>;
    const min = typeof bb.min === "number" && Number.isFinite(bb.min) ? clamp(bb.min, -10000, 10000) : 0;
    const color = isHex(bb.color) ? bb.color.toLowerCase() : "#ffffff";
    bands.push({ min, color });
  }
  bands.sort((a, b) => a.min - b.min);
  const trimmed = bands.slice(0, 5);
  if (trimmed.length < 3) {
    const fallback = DEFAULT_BANDS.health;
    while (trimmed.length < 3) trimmed.push({ ...fallback[trimmed.length] });
    trimmed.sort((a, b) => a.min - b.min);
  }
  return { enabled: true, mode, bands: trimmed };
}

function normalizeLook(raw: unknown): HudLook {
  const d = defaultLook();
  if (!raw || typeof raw !== "object") return d;
  const o = raw as Record<string, unknown>;
  const num = (v: unknown, fallback: number, min: number, max: number): number =>
    typeof v === "number" && Number.isFinite(v) ? clamp(v, min, max) : fallback;
  const color = (v: unknown): string | null => (isHex(v) ? v.toLowerCase() : null);
  return {
    background: color(o.background),
    backgroundOpacity: num(o.backgroundOpacity, d.backgroundOpacity, 0, 1),
    borderColor: color(o.borderColor),
    borderWidth: num(o.borderWidth, 0, 0, 8),
    borderRadius: num(o.borderRadius, d.borderRadius, 0, 32),
    padding: num(o.padding, 0, 0, 32),
    textColor: color(o.textColor),
    trackColor: color(o.trackColor),
    trackOpacity: num(o.trackOpacity, d.trackOpacity, 0, 1),
    fillOpacity: num(o.fillOpacity, 1, 0, 1),
    barRadius: num(o.barRadius, d.barRadius, 0, 32),
  };
}

function normalizeElement(raw: unknown, id: string): HudElementLayout {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const anchor = ANCHORS.includes(o.anchor as HudAnchor) ? (o.anchor as HudAnchor) : "auto";
  const off = (o.offset && typeof o.offset === "object" ? o.offset : {}) as Record<string, unknown>;
  return {
    id,
    anchor,
    offset: {
      x: typeof off.x === "number" && Number.isFinite(off.x) ? clamp(off.x, -2000, 2000) : 0,
      y: typeof off.y === "number" && Number.isFinite(off.y) ? clamp(off.y, -2000, 2000) : 0,
    },
    scale: typeof o.scale === "number" && Number.isFinite(o.scale) ? clamp(o.scale, 0.25, 3) : 1,
    opacity: typeof o.opacity === "number" && Number.isFinite(o.opacity) ? clamp(o.opacity, 0, 1) : 1,
    z:
      typeof o.z === "number" && Number.isFinite(o.z)
        ? clamp(Math.round(o.z), -10, 100)
        : null,
    visible: o.visible !== false,
    removed: o.removed === true,
    raceOnly: typeof o.raceOnly === "boolean" ? o.raceOnly : RACE_ONLY_DEFAULT.has(id),
    look: normalizeLook(o.look),
    color: normalizeColor(o.color),
  };
}

export function normalizeDocument(raw: unknown): HudDocument {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const saved = Array.isArray(o.elements) ? o.elements : [];
  const byId = new Map<string, unknown>();
  for (const e of saved) {
    if (e && typeof e === "object" && typeof (e as Record<string, unknown>).id === "string") {
      byId.set((e as Record<string, unknown>).id as string, e);
    }
  }
  const builtins = HUD_ELEMENTS.map((def) => normalizeElement(byId.get(def.id), def.id));
  // Custom value elements ride in the same document; keep up to 32, unique ids.
  const customs: HudElementLayout[] = [];
  const seen = new Set(HUD_ELEMENTS.map((e) => e.id));
  for (const raw of saved) {
    if (!raw || typeof raw !== "object") continue;
    const r = raw as Record<string, unknown>;
    const id = typeof r.id === "string" ? r.id : "";
    if (!id || seen.has(id)) continue;
    const custom = normalizeCustom(r.custom);
    if (!custom) continue;
    seen.add(id);
    const base = normalizeElement(r, id);
    base.custom = custom;
    customs.push(base);
    if (customs.length >= 32) break;
  }
  return { version: 1, elements: [...builtins, ...customs] };
}

export function normalizeStore(raw: unknown): HudLayoutStore {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  return {
    version: 1,
    playing: normalizeDocument(o.playing),
    spectate: normalizeDocument(o.spectate),
    spectateUsePlayer: o.spectateUsePlayer === true,
  };
}

/** Element ids that can carry a value colour. */
export function elementSource(id: string): HudValueSource | null {
  return HUD_ELEMENT_BY_ID.get(id)?.source ?? null;
}
